import * as React from 'react';
import { BeatMotionSource } from '../motion/BeatMotionSource';
import { BinaryMotionSource } from '../motion/BinaryMotionSource';
import DraggablePoint from '../motion/DraggablePoint';
import { MotionSourceType } from '../motion/MotionSource';
import { MuteMotionSource } from '../motion/MuteMotionSource';
import { getCSSVarValue } from '../utils';

interface Props {
  width: number;
  height: number;
  source: MuteMotionSource;
}

const PADDING = 5;

export class MotionSourceEditView extends React.Component<Props> {
  canvasEl = React.createRef<HTMLCanvasElement>();
  needleEl = React.createRef<HTMLDivElement>();

  componentDidMount() {
    this.updateWave();
  }

  updateWave() {
    if (!this.canvasEl.current) {
      return;
    }
    const loopDuration = this.props.source.getLoopDuration();
    // const curve = this.props.source as BeatMotionSource;
    const ctx = this.canvasEl.current.getContext('2d')!;
    const width = this.props.width;
    const height = this.props.height;
    const isRetina = window.devicePixelRatio > 1;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = getCSSVarValue('--secondary-text-color');
    ctx.globalAlpha = 0.3;

    let x = 0;
    let dx = isRetina ? 0.5 : 1;
    let t = 0;

    while (x < width) {
      t = x / width;
      const level = this.props.source.getLevelAt(t * loopDuration);
      const bandHeight = level * (height - 2 * PADDING);
      // console.log(y);
      ctx.fillRect(x, height - bandHeight - PADDING, 1, bandHeight);
      x += dx;
    }
    ctx.fillRect(0, height - PADDING, width, PADDING);
    // lines
    ctx.strokeStyle = getCSSVarValue('--accent-color');
    ctx.globalAlpha = 1;
    ctx.lineWidth = 2;

    ctx.beginPath();

    switch (this.props.source.type) {
      case MotionSourceType.Beat:
        {
          const curve = this.props.source as BeatMotionSource;
          // left
          ctx.moveTo(0, height - PADDING);
          ctx.lineTo(
            Math.min(curve.centerPointX, curve.leftPointRightDistance) * width -
              5,
            height - PADDING
          );

          // center
          const leftX =
            Math.max(0, curve.centerPointX - curve.centerPointLeftDistance) *
            width;
          if (leftX < curve.centerPointX * width - 10) {
            ctx.moveTo(leftX + 5, PADDING);
            ctx.lineTo(curve.centerPointX * width - 5, PADDING);
            ctx.stroke();
          }
          const rightX =
            Math.min(1, curve.centerPointX + curve.centerPointRightDistance) *
            width;
          if (rightX > curve.centerPointX * width + 10) {
            ctx.moveTo(curve.centerPointX * width + 5, PADDING);
            ctx.lineTo(rightX - 5, PADDING);
          }

          // right
          ctx.moveTo(
            Math.max(curve.centerPointX, 1 - curve.rightPointLeftDistance) *
              width +
              5,
            height - PADDING
          );
          ctx.lineTo(width, height - PADDING);
        }
        break;

      case MotionSourceType.Binary: {
        break;
      }

      default:
        break;
    }

    ctx.stroke();
  }

  didChangeLeftPointRightDistance = (position: number) => {
    const curve = this.props.source as BeatMotionSource;
    curve.leftPointRightDistance = Math.min(curve.centerPointX, position);
    this.updateWave();
    this.forceUpdate();
  };
  didMoveCenter = (position: number) => {
    (this.props.source as BinaryMotionSource).centerPointX = position;
    this.updateWave();
    this.forceUpdate();
  };
  didChangeCenterPointLeftDistance = (position: number) => {
    const curve = this.props.source as BeatMotionSource;
    position = Math.min(curve.centerPointX, position);
    curve.centerPointLeftDistance = curve.centerPointX - position;
    this.updateWave();
    this.forceUpdate();
  };
  didChangeCenterPointRightDistance = (position: number) => {
    const curve = this.props.source as BeatMotionSource;
    position = Math.max(curve.centerPointX, position);
    curve.centerPointRightDistance = position - curve.centerPointX;
    this.updateWave();
    this.forceUpdate();
  };

  didChangeRightPointLeftDistance = (position: number) => {
    const curve = this.props.source as BeatMotionSource;
    curve.rightPointLeftDistance = 1 - Math.max(curve.centerPointX, position);
    this.updateWave();
    this.forceUpdate();
  };

  renderBeatCurve = (): React.ReactNode => {
    const curve = this.props.source as BeatMotionSource;
    const w = this.props.width;
    const centerX = curve.centerPointX * w;

    return (
      <>
        <div className="point point--left" />
        <DraggablePoint
          width={this.props.width}
          style={{ left: centerX, top: 5 }}
          onChange={this.didMoveCenter}
        />
        <div className="point point--right" />
        <DraggablePoint
          width={this.props.width}
          style={{
            top: this.props.height - PADDING,
            left: Math.min(centerX, curve.leftPointRightDistance * w)
          }}
          onChange={this.didChangeLeftPointRightDistance}
        />
        {centerX > 0 && (
          <DraggablePoint
            width={this.props.width}
            style={{
              top: PADDING,
              left: Math.max(0, centerX - curve.centerPointLeftDistance * w)
            }}
            onChange={this.didChangeCenterPointLeftDistance}
          />
        )}
        {centerX < w && (
          <DraggablePoint
            width={this.props.width}
            style={{
              top: PADDING,
              left: Math.min(w, centerX + curve.centerPointRightDistance * w)
            }}
            onChange={this.didChangeCenterPointRightDistance}
          />
        )}
        <DraggablePoint
          width={this.props.width}
          style={{
            top: this.props.height - PADDING,
            left: Math.max(centerX, (1 - curve.rightPointLeftDistance) * w)
          }}
          onChange={this.didChangeRightPointLeftDistance}
        />
      </>
    );
  };

  renderBinary = (): React.ReactNode => {
    const curve = this.props.source as BinaryMotionSource;
    const w = this.props.width;
    const centerX = curve.centerPointX * w;
    return (
      <>
        <DraggablePoint
          width={this.props.width}
          style={{ left: centerX, top: this.props.height * 0.5 }}
          onChange={this.didMoveCenter}
        />
      </>
    );
  };

  render() {
    const { width, height, source } = this.props;
    const sourceType = source.type;
    return (
      <div className="soundtrack">
        <div className="waveform">
          <div className="curve">
            <canvas ref={this.canvasEl} width={width} height={height} />
            <div ref={this.needleEl} className="needle" />
            {sourceType === MotionSourceType.Beat && this.renderBeatCurve()}
            {sourceType === MotionSourceType.Binary && this.renderBinary()}
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/controller/MotionSourceEditView.tsx